/**
 * WorkshopFlowBar — prev / pause-resume / next / stop controls, with the live
 * module's name; Käynnistä while no flow is running.
 * Desktop/iPad: rendered at the PhaseNav sidebar foot.
 * Phone (below sm): sticky bottom bar so advancing is always one thumb tap away.
 */
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { ChevronLeft, ChevronRight, Pause, Play, Square } from 'lucide-react';
import { media } from '../../styles/breakpoints';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const StepCountLabel = styled.div`
  font-size: 11px;
  color: #666;
  text-align: center;
  padding: 4px 0 8px;

  ${media.sm} {
    padding: 0;
    font-size: 12px;
    color: #999;
  }
`;

const ControlStrip = styled.div`
  flex-shrink: 0;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${media.sm} {
    border-top: none;
    padding: 0;
    gap: 10px;
  }
`;

const MobileBar = styled.div`
  display: none;

  ${media.sm} {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 90;
    padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
    background: rgba(10, 10, 16, 0.96);
    backdrop-filter: blur(12px);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.45);
  }
`;

const DesktopWrap = styled.div`
  ${media.sm} {
    display: none;
  }
`;

const CtrlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${p => (p.$primary ? '#4a9eff' : 'rgba(255,255,255,0.08)')};
  border: 1px solid ${p => (p.$primary ? '#4a9eff' : 'rgba(255,255,255,0.12)')};
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: ${p => (p.$primary ? '#3a8eef' : 'rgba(255,255,255,0.15)')};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  ${media.sm} {
    width: 48px;
    height: 48px;
    border-radius: 10px;
  }
`;

const StopButton = styled(CtrlButton)`
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;

  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.25);
  }
`;

const LiveName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 4px 4px;
`;

const StartButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid #22c55e;
  background: #15803d;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  ${media.sm} { min-height: 48px; }
`;

const TimerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 2px 0 6px;

  span {
    font-family: 'JetBrains Mono', monospace;
    font-size: 15px;
    font-weight: 700;
    min-width: 54px;
    text-align: center;
    color: ${(props) => (props.$warning ? '#dc2626' : '#374151')};
  }
  button {
    all: unset;
    min-width: 44px;
    text-align: center;
    padding: 4px 6px;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    font-size: 11px;
    color: #374151;
    cursor: pointer;
    &:hover { background: #f3f4f6; }
  }
`;

/**
 * Remaining step time (#1153): server-computed on every flow-timer-state,
 * ticked down locally between syncs, with ±5 min facilitator nudges.
 */
function StepTimer({ timer, onTimerAdjust }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(timer?.remaining ?? null);
  useEffect(() => {
    setRemaining(timer?.remaining ?? null);
  }, [timer]);
  const paused = Boolean(timer?.isPaused);
  const hasRemaining = remaining != null;
  useEffect(() => {
    if (paused || !hasRemaining) return undefined;
    const tick = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(tick);
  }, [paused, hasRemaining]);

  if (!timer || !timer.total || !hasRemaining) return null;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  return (
    <TimerRow $warning={remaining <= 60 && !paused}>
      {onTimerAdjust && (
        <button type="button" onClick={() => onTimerAdjust(-5 * 60000)} title={t('workshopOrchestrator.timerMinus')}>
          −5 min
        </button>
      )}
      <span>{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}</span>
      {onTimerAdjust && (
        <button type="button" onClick={() => onTimerAdjust(5 * 60000)} title={t('workshopOrchestrator.timerPlus')}>
          +5 min
        </button>
      )}
    </TimerRow>
  );
}

function FlowControls({ currentStepIndex, stepsLength, liveName, isPaused, timer, onFlowAction, onTimerAdjust, gate = null }) {
  const { t } = useTranslation();
  // Seuraava/Edellinen walk screen states before modules (the first module opens
  // with the welcome cube), so neither end disables them; the tick reports 'end'.
  return (
    <>
      {liveName && <LiveName title={liveName}>● {liveName}</LiveName>}
      <StepCountLabel>
        {t('workshopOrchestrator.stepOf', { current: currentStepIndex + 1, total: stepsLength })}
      </StepCountLabel>
      <StepTimer timer={timer} onTimerAdjust={onTimerAdjust} />
      {/* The live module's gate (its admin.checks), not its buttons: those are in its section (#1217) */}
      {gate}
      <ControlStrip>
        <CtrlButton
          type="button"
          onClick={() => onFlowAction('previous')}
          title={t('workshopOrchestrator.previous')}
          aria-label={t('workshopOrchestrator.previous')}
        >
          <ChevronLeft size={18} />
        </CtrlButton>
        <CtrlButton
          type="button"
          $primary
          onClick={() => onFlowAction(isPaused ? 'resume' : 'pause')}
          title={isPaused ? t('workshopOrchestrator.resume') : t('workshopOrchestrator.pause')}
          aria-label={isPaused ? t('workshopOrchestrator.resume') : t('workshopOrchestrator.pause')}
        >
          {isPaused ? <Play size={16} /> : <Pause size={16} />}
        </CtrlButton>
        <CtrlButton
          type="button"
          onClick={() => onFlowAction('next')}
          title={t('workshopOrchestrator.next')}
          aria-label={t('workshopOrchestrator.next')}
        >
          <ChevronRight size={18} />
        </CtrlButton>
        <StopButton
          type="button"
          onClick={() => onFlowAction('stop')}
          title={t('workshopOrchestrator.stop')}
          aria-label={t('workshopOrchestrator.stop')}
        >
          <Square size={14} />
        </StopButton>
      </ControlStrip>
    </>
  );
}

function StartControl({ onStart }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <ControlStrip>
      <StartButton
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try { await onStart(); } finally { setBusy(false); }
        }}
      >
        <Play size={16} />
        {t('workshopOrchestrator.start')}
      </StartButton>
    </ControlStrip>
  );
}

/**
 * @param {'desktop' | 'mobile'} variant
 * @param {() => Promise<void>} [onStart] shown as Käynnistä while no flow is running
 */
export default function WorkshopFlowBar({
  steps = [],
  currentStepId,
  flowState,
  timer = null,
  onFlowAction,
  onTimerAdjust = null,
  onStart = null,
  variant = 'desktop',
  gate = null,
}) {
  if (!flowState && !onStart) return null;

  let content;
  if (!flowState) {
    content = <StartControl onStart={onStart} />;
  } else {
    const currentStepIndex = steps.findIndex((s) => s.id === currentStepId);
    content = (
      <FlowControls
        currentStepIndex={currentStepIndex < 0 ? 0 : currentStepIndex}
        stepsLength={steps.length}
        liveName={steps[currentStepIndex]?.name}
        isPaused={flowState?.isPaused}
        timer={timer}
        onFlowAction={onFlowAction}
        onTimerAdjust={onTimerAdjust}
        gate={gate}
      />
    );
  }

  return variant === 'mobile' ? <MobileBar>{content}</MobileBar> : <DesktopWrap>{content}</DesktopWrap>;
}
